import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/ui/TopNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FileUploadZone from './components/FileUploadZone';
import ImportedQRCard from './components/ImportedQRCard';
import ImportOptionsPanel from './components/ImportOptionsPanel';
import CampaignSelector from './components/CampaignSelector';
import BulkActionsPanel from './components/BulkActionsPanel';
import { QRAnalyticsService } from '../../services/qrAnalyticsService';
import { QRDynamicRedirectService } from '../../services/qrDynamicRedirectService';
import { supabase } from '../../lib/supabase';


const QRImportCenter = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // State for imported QR codes
  const [importedQRs, setImportedQRs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  
  // State for import options
  const [importOptions, setImportOptions] = useState({
    enableDynamicLinks: true,
    autoDetectType: true,
    duplicateHandling: 'create_new', // 'create_new', 'skip', 'update'
    defaultCampaign: null,
    defaultPrivacy: 'basic'
  });

  // State for bulk operations
  const [selectedQRs, setSelectedQRs] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // State for campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // State for success/error handling
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Load campaigns on mount
  useEffect(() => {
    if (user) {
      loadCampaigns();
    }
  }, [user]);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const { data, error } = await QRAnalyticsService?.getCampaigns();
      if (!error) {
        setCampaigns(data || []);
      }
    } catch (error) {
      console.log('Error loading campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // QR code type detection based on content
  const detectQRType = (content) => {
    if (!content) return 'text';
    
    const urlRegex = /^https?:\/\//i;
    const emailRegex = /^mailto:/i;
    const phoneRegex = /^tel:/i;
    const smsRegex = /^sms:/i;
    const whatsappRegex = /^https?:\/\/(wa\.me|whatsapp\.com)/i;
    const wifiRegex = /^WIFI:/i;
    const vcardRegex = /^BEGIN:VCARD/i;

    if (urlRegex?.test(content)) {
      if (whatsappRegex?.test(content)) return 'whatsapp';
      return 'url';
    }
    if (emailRegex?.test(content) || content?.includes('@') && !content?.includes('\n')) return 'email';
    if (phoneRegex?.test(content) || /^\+?[\d\s\-\(\)]+$/?.test(content?.trim())) return 'phone';
    if (smsRegex?.test(content)) return 'sms';
    if (wifiRegex?.test(content)) return 'wifi';
    if (vcardRegex?.test(content)) return 'vcard';
    
    return 'text';
  };

  // Extract styling information from QR code image
  const extractQRStyling = async (imageData, canvas) => {
    return new Promise((resolve) => {
      try {
        const ctx = canvas?.getContext('2d');
        if (!ctx) {
          resolve({
            size: 256,
            foregroundColor: '#000000',
            backgroundColor: '#ffffff',
            errorCorrectionLevel: 'M'
          });
          return;
        }

        // Sample pixels to determine colors
        const imageDataObj = ctx?.getImageData(0, 0, canvas?.width, canvas?.height);
        const pixels = imageDataObj?.data;
        
        // Find most common dark and light colors
        const colorCounts = {};
        for (let i = 0; i < pixels?.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const brightness = (r + g + b) / 3;
          
          if (brightness < 128) {
            // Dark color - likely foreground
            const hex = `#${r?.toString(16)?.padStart(2, '0')}${g?.toString(16)?.padStart(2, '0')}${b?.toString(16)?.padStart(2, '0')}`;
            colorCounts[hex] = (colorCounts[hex] || 0) + 1;
          }
        }

        const foregroundColor = Object.keys(colorCounts)?.sort((a, b) => colorCounts[b] - colorCounts[a])?.[0] || '#000000';

        resolve({
          size: Math.max(canvas?.width, canvas?.height),
          foregroundColor,
          backgroundColor: '#ffffff',
          errorCorrectionLevel: 'M',
          extracted: true
        });
      } catch (error) {
        console.log('Error extracting styling:', error);
        resolve({
          size: 256,
          foregroundColor: '#000000',
          backgroundColor: '#ffffff',
          errorCorrectionLevel: 'M'
        });
      }
    });
  };

  // Enhanced QR code decoding with multiple fallback methods
  const decodeQRFromImage = async (imageFile, img, canvas) => {
    const decodingResults = [];
    
    try {
      // Method 1: qr-scanner library (primary)
      try {
        const { QrScanner } = await import('qr-scanner');
        const qrContent = await QrScanner?.scanImage(img, null, null, null, {
          returnDetailedScanResult: false,
          scanRegion: null
        });
        if (qrContent) {
          decodingResults?.push({ method: 'qr-scanner', content: qrContent });
        }
      } catch (scannerError) {
        console.log('QrScanner failed:', scannerError?.message);
      }

      // Method 2: jsQR library (fallback)
      if (decodingResults?.length === 0) {
        try {
          const jsQR = await import('jsqr');
          const ctx = canvas?.getContext('2d');
          let imageData = ctx?.getImageData(0, 0, canvas?.width, canvas?.height);
          const code = jsQR?.default(imageData?.data, imageData?.width, imageData?.height, {
            inversionAttempts: "dontInvert",
          });
          if (code?.data) {
            decodingResults?.push({ method: 'jsqr', content: code?.data });
          }
        } catch (jsqrError) {
          console.log('jsQR failed:', jsqrError?.message);
        }
      }

      // Method 3: Enhanced jsQR with preprocessing (second fallback)
      if (decodingResults?.length === 0) {
        try {
          const jsQR = await import('jsqr');
          const ctx = canvas?.getContext('2d');
          
          // Try with inverted colors
          let imageData = ctx?.getImageData(0, 0, canvas?.width, canvas?.height);
          const code = jsQR?.default(imageData?.data, imageData?.width, imageData?.height, {
            inversionAttempts: "attemptBoth",
          });
          if (code?.data) {
            decodingResults?.push({ method: 'jsqr-inverted', content: code?.data });
          }
        } catch (jsqrInvertedError) {
          console.log('jsQR inverted failed:', jsqrInvertedError?.message);
        }
      }

      // Method 4: Canvas preprocessing + retry (third fallback)
      if (decodingResults?.length === 0) {
        try {
          const preprocessResult = await tryWithPreprocessing(img, canvas);
          if (preprocessResult) {
            decodingResults?.push({ method: 'preprocessed', content: preprocessResult });
          }
        } catch (preprocessError) {
          console.log('Preprocessing failed:', preprocessError?.message);
        }
      }

      // Method 5: qrcode-parser as final fallback
      if (decodingResults?.length === 0) {
        try {
          const qrcodeParser = await import('qrcode-parser');
          const result = await qrcodeParser?.default(imageFile);
          if (result) {
            decodingResults?.push({ method: 'qrcode-parser', content: result });
          }
        } catch (parserError) {
          console.log('QRCode parser failed:', parserError?.message);
        }
      }

      return decodingResults?.length > 0 ? decodingResults?.[0]?.content : null;

    } catch (error) {
      console.log('All QR decoding methods failed:', error?.message);
      return null;
    }
  };

  // Image preprocessing helper
  const tryWithPreprocessing = async (img, originalCanvas) => {
    const jsQR = await import('jsqr');
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    
    // Try different preprocessing techniques
    const techniques = [
      // Contrast enhancement
      (imageData) => {
        const data = imageData?.data;
        const factor = 259 * (128 + 255) / (255 * (259 - 128));
        for (let i = 0; i < data?.length; i += 4) {
          data[i] = Math.max(0, Math.min(255, factor * (data?.[i] - 128) + 128));
          data[i + 1] = Math.max(0, Math.min(255, factor * (data?.[i + 1] - 128) + 128));
          data[i + 2] = Math.max(0, Math.min(255, factor * (data?.[i + 2] - 128) + 128));
        }
        return imageData;
      },
      // Grayscale conversion
      (imageData) => {
        const data = imageData?.data;
        for (let i = 0; i < data?.length; i += 4) {
          const gray = data?.[i] * 0.299 + data?.[i + 1] * 0.587 + data?.[i + 2] * 0.114;
          data[i] = gray;
          data[i + 1] = gray;
          data[i + 2] = gray;
        }
        return imageData;
      },
      // Threshold (black & white)
      (imageData) => {
        const data = imageData?.data;
        for (let i = 0; i < data?.length; i += 4) {
          const gray = data?.[i] * 0.299 + data?.[i + 1] * 0.587 + data?.[i + 2] * 0.114;
          const bw = gray > 128 ? 255 : 0;
          data[i] = bw;
          data[i + 1] = bw;
          data[i + 2] = bw;
        }
        return imageData;
      }
    ];

    for (const technique of techniques) {
      canvas.width = img?.width;
      canvas.height = img?.height;
      ctx?.drawImage(img, 0, 0);
      
      let imageData = ctx?.getImageData(0, 0, canvas?.width, canvas?.height);
      imageData = technique(imageData);
      
      const code = jsQR?.default(imageData?.data, imageData?.width, imageData?.height, {
        inversionAttempts: "attemptBoth",
      });
      
      if (code?.data) {
        return code?.data;
      }
    }
    
    return null;
  };

  // Enhanced file processing with better error handling and image support
  const handleFilesImported = useCallback(async (files) => {
    if (!files?.length) return;

    setIsProcessing(true);
    setProcessingStatus('Processing files...');
    
    const results = [];

    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      
      try {
        setProcessingStatus(`Processing ${file?.name} (${i + 1}/${files?.length})`);

        // Validate file type first
        const isValidImageType = file?.type?.startsWith('image/') || 
          ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/bmp', 'image/tiff']?.some(type => 
            file?.type === type || file?.name?.toLowerCase()?.endsWith(type?.replace('image/', ''))
          );

        if (!isValidImageType && file?.type !== 'application/pdf') {
          results?.push({
            id: `import_${Date.now()}_${i}`,
            fileName: file?.name,
            originalFile: file,
            status: 'error',
            error: `Unsupported file type: ${file?.type}. Please use PNG, JPEG, JPG, WEBP, BMP, TIFF, or PDF files.`,
            metadata: {
              importedFrom: file?.name,
              importedAt: new Date()?.toISOString(),
              fileSize: file?.size,
              fileType: file?.type
            }
          });
          continue;
        }

        if (file?.type === 'application/pdf') {
          // Handle PDF files separately (would need PDF.js integration)
          results?.push({
            id: `import_${Date.now()}_${i}`,
            fileName: file?.name,
            originalFile: file,
            status: 'error',
            error: 'PDF scanning is not yet implemented. Please use image files for now.',
            metadata: {
              importedFrom: file?.name,
              importedAt: new Date()?.toISOString(),
              fileSize: file?.size,
              fileType: file?.type
            }
          });
          continue;
        }

        // Create image with better error handling
        const img = new Image();
        const imageUrl = URL.createObjectURL(file);
        
        try {
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error('Failed to load image'));
            img.crossOrigin = 'anonymous';
            img.src = imageUrl;
          });

          // Create canvas for analysis with better size handling
          const canvas = document.createElement('canvas');
          const ctx = canvas?.getContext('2d');
          
          if (!ctx) {
            throw new Error('Could not create canvas context');
          }

          // Ensure reasonable canvas size to avoid memory issues
          const maxSize = 2048;
          let { width, height } = img;
          
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          // Enhanced QR decoding with multiple methods
          const qrContent = await decodeQRFromImage(file, img, canvas);
          
          if (qrContent) {
            // Extract styling information
            const styling = await extractQRStyling(null, canvas);
            
            // Auto-detect QR type
            const detectedType = importOptions?.autoDetectType ? detectQRType(qrContent) : 'text';
            
            // Check for duplicates if enabled
            let isDuplicate = false;
            if (importOptions?.duplicateHandling !== 'create_new') {
              try {
                const { data: existingQRs } = await QRAnalyticsService?.searchQRCodes(qrContent);
                isDuplicate = existingQRs?.length > 0;
              } catch (error) {
                console.log('Error checking duplicates:', error);
              }
            }

            const importedQR = {
              id: `import_${Date.now()}_${i}`,
              fileName: file?.name,
              originalFile: file,
              content: qrContent,
              detectedType: detectedType,
              name: `Imported from ${file?.name}`,
              styling: styling,
              campaign: importOptions?.defaultCampaign,
              status: isDuplicate && importOptions?.duplicateHandling === 'skip' ? 'skipped' : 'ready',
              isDuplicate,
              metadata: {
                importedFrom: file?.name,
                importedAt: new Date()?.toISOString(),
                fileSize: file?.size,
                fileType: file?.type,
                originalDimensions: { width: img?.width, height: img?.height },
                processedDimensions: { width, height }
              }
            };

            results?.push(importedQR);
          } else {
            // Enhanced error reporting for failed decoding
            const errorDetails = [
              'Could not decode QR code from this image.',
              'Please ensure:',
              '• The QR code is clearly visible and not distorted',
              '• The image has good contrast (dark QR code on light background)',
              '• The QR code takes up a significant portion of the image',
              '• The image is not rotated or skewed',
              '• Try uploading a higher resolution image if possible'
            ]?.join(' ');

            results?.push({
              id: `import_${Date.now()}_${i}`,
              fileName: file?.name,
              originalFile: file,
              status: 'error',
              error: errorDetails,
              metadata: {
                importedFrom: file?.name,
                importedAt: new Date()?.toISOString(),
                fileSize: file?.size,
                fileType: file?.type,
                originalDimensions: { width: img?.width, height: img?.height },
                decodingAttempted: true
              }
            });
          }
        } catch (imageError) {
          results?.push({
            id: `import_${Date.now()}_${i}`,
            fileName: file?.name,
            originalFile: file,
            status: 'error',
            error: `Failed to process image: ${imageError?.message || 'Unknown error'}. Please try a different image format or ensure the file is not corrupted.`,
            metadata: {
              importedFrom: file?.name,
              importedAt: new Date()?.toISOString(),
              fileSize: file?.size,
              fileType: file?.type
            }
          });
        }

        // Cleanup
        URL.revokeObjectURL(imageUrl);
      } catch (error) {
        results?.push({
          id: `import_${Date.now()}_${i}`,
          fileName: file?.name,
          originalFile: file,
          status: 'error',
          error: error?.message || 'Failed to process file',
          metadata: {
            importedFrom: file?.name,
            importedAt: new Date()?.toISOString(),
            fileSize: file?.size,
            fileType: file?.type
          }
        });
      }
    }

    setImportedQRs(results);
    setIsProcessing(false);
    setProcessingStatus('');
  }, [importOptions]);

  // Handle QR code updates
  const handleQRUpdate = (qrId, updates) => {
    setImportedQRs(prev => prev?.map(qr => 
      qr?.id === qrId ? { ...qr, ...updates } : qr
    ));
  };

  // Handle QR code removal
  const handleQRRemove = (qrId) => {
    setImportedQRs(prev => prev?.filter(qr => qr?.id !== qrId));
    setSelectedQRs(prev => prev?.filter(id => id !== qrId));
  };

  // Handle bulk selection
  const handleBulkSelection = (qrIds, selected) => {
    if (selected) {
      setSelectedQRs(prev => [...new Set([...prev, ...qrIds])]);
    } else {
      setSelectedQRs(prev => prev?.filter(id => !qrIds?.includes(id)));
    }
  };

  // Handle bulk save to database
  const handleBulkSave = async (qrIds = null) => {
    const qrsToSave = qrIds ? 
      importedQRs?.filter(qr => qrIds?.includes(qr?.id) && qr?.status === 'ready') :
      importedQRs?.filter(qr => qr?.status === 'ready');

    if (!qrsToSave?.length) {
      return;
    }

    setIsProcessing(true);
    setProcessingStatus('Saving QR codes to database...');

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (const qr of qrsToSave) {
      try {
        // Prepare QR code data for database with correct field names
        const qrCodePayload = {
          name: qr?.name?.trim() || `Imported from ${qr?.fileName}`,
          content: qr?.content,
          qr_type: qr?.detectedType || 'text', // Use correct field name from schema
          campaign_id: qr?.campaign?.id || null,
          owner_id: user?.id,
          metadata: {
            ...qr?.metadata,
            styling: qr?.styling,
            imported: true,
            original_file: qr?.fileName,
            import_date: new Date()?.toISOString()
          },
          is_active: true
        };

        // Create QR code in database
        const { data: savedQR, error: saveError } = await QRAnalyticsService?.createQRCode(qrCodePayload);

        if (saveError) {
          results.failed++;
          results?.errors?.push(`${qr?.name}: ${saveError}`);
          console.log(`Failed to save QR ${qr?.name}:`, saveError);
          continue;
        }

        if (!savedQR) {
          results.failed++;
          results?.errors?.push(`${qr?.name}: No data returned from save operation`);
          continue;
        }

        results.success++;
        
        // Add dynamic redirect if enabled and we have a saved QR with short_url
        if (importOptions?.enableDynamicLinks && savedQR?.id && savedQR?.short_url) {
          try {
            const { error: redirectError } = await QRDynamicRedirectService?.addDynamicRedirect(
              savedQR?.id,
              qr?.content,
              user?.id
            );
            
            if (redirectError) {
              console.log('Warning: Could not create dynamic redirect:', redirectError);
              // Don't fail the save operation for redirect issues
            }
          } catch (redirectError) {
            console.log('Warning: Exception creating dynamic redirect:', redirectError);
          }
        }

        // Update QR status to saved with actual database info
        handleQRUpdate(qr?.id, { 
          status: 'saved', 
          savedId: savedQR?.id,
          shortCode: savedQR?.short_url,
          trackableUrl: savedQR?.short_url ? `${window.location?.origin}/qr/${savedQR?.short_url}` : null
        });

      } catch (error) {
        results.failed++;
        results?.errors?.push(`${qr?.name}: ${error?.message || 'Unknown error'}`);
        console.log(`Exception saving QR ${qr?.name}:`, error);
      }
    }

    // Create user activity record for the batch import
    try {
      const userName = user?.user_metadata?.name || user?.email?.split('@')?.[0] || 'User';
      
      const { error: activityError } = await supabase
        ?.from('user_activities')
        ?.insert([{
          user_id: user?.id,
          activity_type: 'qr_batch_import',
          activity_data: {
            action: 'qr_batch_import',
            success_count: results?.success,
            failed_count: results?.failed,
            timestamp: new Date()?.toISOString(),
            imported_files: qrsToSave?.map(qr => qr?.fileName)
          }
        }]);
        
      if (activityError) {
        console.log('Warning: Could not create activity record:', activityError);
      }
    } catch (activityRecordError) {
      console.log('Warning: Could not create activity record:', activityRecordError);
    }

    setImportResults(results);
    setShowResults(true);
    setIsProcessing(false);
    setProcessingStatus('');
  };

  // Statistics for imported QRs
  const importStats = {
    total: importedQRs?.length,
    ready: importedQRs?.filter(qr => qr?.status === 'ready')?.length,
    errors: importedQRs?.filter(qr => qr?.status === 'error')?.length,
    saved: importedQRs?.filter(qr => qr?.status === 'saved')?.length,
    skipped: importedQRs?.filter(qr => qr?.status === 'skipped')?.length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to access the QR Import Center</p>
          <button
            onClick={() => window.location.href = '/auth'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation />
      <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  QR Import Center
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  Import and analyze existing QR codes from various file formats
                </p>
              </div>
              
              {importedQRs?.length > 0 && (
                <div className="hidden sm:flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    {importStats?.total} files • {importStats?.ready} ready • {importStats?.errors} errors
                  </div>
                  <Button
                    onClick={() => navigate('/qr-library')}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <Icon name="Library" size={16} />
                    <span>View Library</span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Left Panel - Import Options */}
            <div className="lg:col-span-4 space-y-6">
              {/* Import Options Panel */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <ImportOptionsPanel
                  options={importOptions}
                  onOptionsChange={setImportOptions}
                />
              </div>

              {/* Campaign Selector */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <CampaignSelector
                  campaigns={campaigns}
                  selectedCampaign={importOptions?.defaultCampaign}
                  onCampaignSelect={(campaign) => 
                    setImportOptions(prev => ({ ...prev, defaultCampaign: campaign }))
                  }
                  loading={loadingCampaigns}
                  onRefresh={loadCampaigns}
                />
              </div>

              {/* Statistics Panel */}
              {importedQRs?.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Files</span>
                      <span className="font-medium text-gray-900">{importStats?.total}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600">Ready to Save</span>
                      <span className="font-medium text-green-700">{importStats?.ready}</span>
                    </div>
                    {importStats?.saved > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-600">Successfully Saved</span>
                        <span className="font-medium text-blue-700">{importStats?.saved}</span>
                      </div>
                    )}
                    {importStats?.errors > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-red-600">Errors</span>
                        <span className="font-medium text-red-700">{importStats?.errors}</span>
                      </div>
                    )}
                    {importStats?.skipped > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-yellow-600">Skipped (Duplicates)</span>
                        <span className="font-medium text-yellow-700">{importStats?.skipped}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Upload Zone and Results */}
            <div className="lg:col-span-8 space-y-6">
              {/* File Upload Zone */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <FileUploadZone
                  onFilesImported={handleFilesImported}
                  isProcessing={isProcessing}
                  processingStatus={processingStatus}
                  acceptedFormats={['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'application/pdf']}
                />
              </div>

              {/* Bulk Actions Panel */}
              {importedQRs?.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <BulkActionsPanel
                    importedQRs={importedQRs}
                    selectedQRs={selectedQRs}
                    onBulkSelection={handleBulkSelection}
                    onBulkSave={handleBulkSave}
                    isProcessing={isProcessing}
                  />
                </div>
              )}

              {/* Imported QR Cards */}
              {importedQRs?.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Imported QR Codes ({importedQRs?.length})
                  </h3>
                  <div className="grid gap-4">
                    {importedQRs?.map((qr) => (
                      <ImportedQRCard
                        key={qr?.id}
                        qrData={qr}
                        onUpdate={handleQRUpdate}
                        onRemove={handleQRRemove}
                        campaigns={campaigns}
                        isSelected={selectedQRs?.includes(qr?.id)}
                        onSelect={(selected) => handleBulkSelection([qr?.id], selected)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Import Results Modal */}
      {showResults && importResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Import Complete!
              </h3>
              <div className="text-gray-600 mb-6">
                <p className="mb-2">
                  <span className="font-medium text-green-600">{importResults?.success}</span> QR codes saved successfully
                </p>
                {importResults?.failed > 0 && (
                  <p className="text-red-600">
                    <span className="font-medium">{importResults?.failed}</span> failed to save
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/qr-library')}
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Icon name="Library" size={16} className="mr-2" />
                  View QR Library
                </Button>
                <Button
                  onClick={() => {
                    setShowResults(false);
                    setImportResults(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Continue Importing
                </Button>
              </div>
              
              {importResults?.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                  <div className="text-xs text-red-600 max-h-24 overflow-y-auto">
                    {importResults?.errors?.map((error, index) => (
                      <div key={index} className="mb-1">{error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRImportCenter;